// src/client.ts
import { createAuthClient } from "better-auth/react";
function createBetterAuthClient(baseUrl) {
  return createAuthClient({
    baseURL: baseUrl ?? `${window.location.origin}/api/auth`,
    fetchOptions: {
      credentials: "include"
    }
  });
}
var _client;
function getBetterAuthClient() {
  if (!_client) {
    _client = createBetterAuthClient();
  }
  return _client;
}

// src/hooks.ts
function lazyProxy(key) {
  const noopTarget = (() => void 0);
  return new Proxy(noopTarget, {
    get(_target, prop) {
      return getBetterAuthClient()[key][prop];
    },
    apply(_target, _thisArg, args) {
      return getBetterAuthClient()[key](...args);
    }
  });
}
function useSession() {
  return getBetterAuthClient().useSession();
}
var signIn = lazyProxy("signIn");
var signOut = lazyProxy("signOut");
function useAuthSession() {
  const { data } = useSession();
  return data ? { user: { id: data.user.id, email: data.user.email, name: data.user.name } } : null;
}
function useAuthor() {
  return useAuthSession()?.user.id ?? null;
}
function useAuthorState() {
  const { data, isPending } = useSession();
  if (isPending) return { settled: false };
  return { settled: true, author: data ? data.user.id : null };
}

// src/useAnonClaim.ts
import {
  ANON_IDENTITY,
  AnonStoreBusyError,
  adoptAnonStore,
  storeName
} from "@jbroll/rowboat-client";
import { evictGraph } from "@jbroll/rowboat-react";
import { useCallback, useEffect, useRef, useState } from "react";
function useAnonClaim(opts) {
  const session = useAuthorState();
  const author = session.settled ? session.author : void 0;
  const scopeColumn = opts.scopeColumn ?? "owner_group_id";
  const [claiming, setClaiming] = useState(false);
  const [blockedByOtherTab, setBlockedByOtherTab] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const blockedRef = useRef(false);
  const claimedForRef = useRef(null);
  const authorRef = useRef(null);
  const queueRef = useRef(Promise.resolve());
  const queuedRef = useRef(0);
  const latestRef = useRef(/* @__PURE__ */ new Map());
  const requestRef = useRef(0);
  const report = (e) => {
    if (opts.onError) opts.onError(e);
    else throw e;
  };
  useEffect(() => {
    if (author === void 0) return;
    authorRef.current = author;
    if (author === null) {
      claimedForRef.current = null;
      if (blockedRef.current) {
        blockedRef.current = false;
        setBlockedByOtherTab(null);
        if (queuedRef.current === 0) setClaiming(false);
      }
      return;
    }
    if (claimedForRef.current === author) return;
    claimedForRef.current = author;
    const userId = author;
    const request = ++requestRef.current;
    latestRef.current.set(userId, request);
    queuedRef.current += 1;
    setClaiming(true);
    const claim = queueRef.current.then(async () => {
      if (authorRef.current !== userId || latestRef.current.get(userId) !== request) return;
      blockedRef.current = false;
      setBlockedByOtherTab(null);
      try {
        await adoptAnonStore({
          app: opts.app,
          accountId: userId,
          scopeColumn,
          scopeGroupId: userId,
          mintScope: opts.mintScope,
          tables: opts.tables,
          migrations: opts.migrations,
          options: opts.options,
          closeAnon: () => evictGraph(storeName(opts.app, ANON_IDENTITY))
        });
      } catch (e) {
        if (!(e instanceof AnonStoreBusyError)) throw e;
        blockedRef.current = true;
        setBlockedByOtherTab(e.store);
        return;
      }
      opts.onClaimed?.(userId);
    });
    const settle = () => {
      queuedRef.current -= 1;
      if (queuedRef.current === 0 && !blockedRef.current) setClaiming(false);
    };
    claim.catch(report);
    queueRef.current = claim.then(settle, settle);
  }, [author, attempt]);
  const retry = useCallback(() => {
    claimedForRef.current = null;
    setAttempt((n) => n + 1);
  }, []);
  return { claiming, blockedByOtherTab, retry };
}
export {
  createBetterAuthClient,
  getBetterAuthClient,
  signIn,
  signOut,
  useAnonClaim,
  useAuthSession,
  useAuthor,
  useSession
};
